/**
 * Browser notification utility
 * Handles Web Notifications API interactions with in-page toast fallback
 */

import { showToastNotification } from '../components/ToastNotification';

export const isBrowserNotificationSupported = (): boolean => {
  return 'Notification' in window;
};

export const getBrowserNotificationPermission = (): NotificationPermission | null => {
  if (!isBrowserNotificationSupported()) return null;
  return Notification.permission;
};

export const requestBrowserNotificationPermission = async (): Promise<NotificationPermission | null> => {
  if (!isBrowserNotificationSupported()) return null;

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission !== 'denied') {
    return await Notification.requestPermission();
  }

  return Notification.permission;
};

export interface BrowserNotificationOptions {
  body?: string;
  icon?: string;
  tag?: string;
  onClick?: () => void;
}

/**
 * Show a notification - always shows in-page toast for reliability,
 * and also attempts browser notification if available (for when user is in another tab)
 */
export const showBrowserNotification = (
  title: string,
  options: BrowserNotificationOptions = {}
): Notification | null => {
  console.log('[BrowserNotification] Attempting to show notification:', title);

  // Always show in-page toast notification for guaranteed visibility
  showToastNotification(title, {
    body: options.body,
    icon: options.icon,
    tag: options.tag,
    onClick: options.onClick,
  });

  // Also try browser notification (useful when user is in another tab/app)
  const canUseBrowserNotification =
    isBrowserNotificationSupported() &&
    Notification.permission === 'granted';

  if (!canUseBrowserNotification) {
    console.log('[BrowserNotification] Browser notifications not available, toast shown');
    return null;
  }

  console.log('[BrowserNotification] Permission status:', Notification.permission);

  try {
    // Create the browser notification (in addition to toast)
    const notification = new Notification(title, {
      body: options.body,
      icon: options.icon || '/logo_icon_only.png',
      tag: options.tag,
      requireInteraction: false,
      silent: false,
    });

    console.log('[BrowserNotification] Browser notification created successfully');

    notification.onshow = () => {
      console.log('[BrowserNotification] Browser notification shown');
    };

    notification.onerror = (e) => {
      console.error('[BrowserNotification] Browser notification error:', e);
    };

    if (options.onClick) {
      notification.onclick = () => {
        window.focus();
        options.onClick?.();
        notification.close();
      };
    }

    return notification;
  } catch (err) {
    console.error('[BrowserNotification] Failed to create browser notification:', err);
    return null;
  }
};

/**
 * Show notification with permission request if needed
 * Always shows toast, attempts browser notification if permitted
 */
export const showBrowserNotificationWithPermission = async (
  title: string,
  options: BrowserNotificationOptions = {}
): Promise<Notification | null> => {
  // Request permission if not yet decided
  if (isBrowserNotificationSupported() && Notification.permission === 'default') {
    await Notification.requestPermission();
  }

  // showBrowserNotification handles both toast and browser notification
  return showBrowserNotification(title, options);
};
